import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Coaching schedule-request builders.
 *
 * Replaces the employee-only `submitNonTaRequest` / `submitTasRequest` methods
 * on `tio-employee/app/adapters/application.ts` (the catch-all application
 * adapter). Those methods did NOT use `this.ajax`/`buildURL`; they issued a
 * raw `fetch` to `${this.host}/coaching/schedule-loan` and
 * `${this.host}/coaching/schedule-tas` (host = `ENV.apiHost`, no namespace),
 * attaching the adapter's auth headers and returning `response.ok === true`
 * (a boolean), swallowing network errors as `false`.
 *
 * The TIO auth handler adds `x-api-key`, `Content-Type`, `Time-Zone`, and
 * `X-Request-Id` to every outgoing request (same as `requests/login.ts`), so
 * the hand-rolled header bundle is dropped here. The boolean-return / error
 * swallowing contract is NOT part of the wire and is preserved at the call
 * site (`mentoring.gts` wraps `store.request` in try/catch → boolean), since
 * `store.request` resolves on 2xx and rejects otherwise.
 *
 * Production `setBuildURLConfig` uses `{ host: apiHost, namespace: '' }`
 * (tio-employee/app/app.ts), so `buildBaseURL({ resourcePath: 'coaching' })`
 * resolves to `${apiHost}/coaching` — byte-identical to the legacy raw URL.
 */

const coachingUrl = (path: string): string => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'coaching' },
    op: 'query',
    resourcePath: 'coaching',
  };
  return `${buildBaseURL(opts)}/${path.replace(/^\/+/, '')}`;
};

/**
 * POST /coaching/schedule-loan
 * body `{ scheduleRequestContent }`
 *
 * Mirrors `ApplicationAdapter#submitNonTaRequest`, which sent
 * `fetch(`${this.host}/coaching/schedule-loan`, { method: 'POST', body:
 * JSON.stringify({ scheduleRequestContent }) })`.
 */
export function submitNonTaCoachingRequest(scheduleRequestContent: string): RequestInfo {
  return {
    url: coachingUrl('schedule-loan'),
    method: 'POST',
    body: JSON.stringify({ scheduleRequestContent }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /coaching/schedule-tas
 * body `{ mentorRequestContent }`
 *
 * Mirrors `ApplicationAdapter#submitTasRequest`, which sent
 * `fetch(`${this.host}/coaching/schedule-tas`, { method: 'POST', body:
 * JSON.stringify({ mentorRequestContent }) })`.
 */
export function submitTasCoachingRequest(mentorRequestContent: string): RequestInfo {
  return {
    url: coachingUrl('schedule-tas'),
    method: 'POST',
    body: JSON.stringify({ mentorRequestContent }),
    cacheOptions: { [SkipCache]: true },
  };
}
